import React from 'react';
import type { PatientDetailsPatientProp } from './internal';
import {
  getValueOrNotSpecifiedString,
  PatientDetailsSection
} from './internal';
import { HBox, VBox } from '../layout';
import { Icon } from '../basic';
import { DefaultDateFormatter, intl, intlCountry } from '../../lib';
import { calculateYearsForDate, getGenderIcon } from '../../util';
import type { PatientDetailsProps } from './PatientDetails';

/**
 * Props for a {@link PatientDetailsPersonDetails} component.
 * @author agd
 */
export type PatientDetailsPersonDetailsProps = PatientDetailsPatientProp &
  Pick<PatientDetailsProps, 'ssnHidden' | 'motherMaidenNameHidden'> & {
    birthPlaceHidden?: boolean;
  };

/**
 * Component for displaying a person's personal details (name, gender, SSN, birhtdate, etc.) for displaying.
 *
 * @param properties a {@link PatientDetailsPersonDetailsProps} object
 * @return the created {@link JSX.Element}.
 * @author agd
 * @constructor
 */
export function PatientDetailsPersonDetails({
  patient,
  ssnHidden,
  motherMaidenNameHidden,
  birthPlaceHidden
}: PatientDetailsPersonDetailsProps): JSX.Element {
  const genderIcon = getGenderIcon(patient?.gender);
  const birthPlace = patient.addresses?.find(addr => addr.addressType === 'BR');
  return (
    <PatientDetailsSection
      className="ehs-patient-details-person-details"
      // TODO: should we have a common rendering method for this? different places seem to do name rendering in a different way
      title={`${patient.name?.givenName || ''} ${
        patient.name?.familyName || ''
      }`}
    >
      <HBox className="person">
        {genderIcon && <Icon {...genderIcon} className="gender-icon" />}
        <VBox>
          {patient.birthdate && (
            <span>
              {DefaultDateFormatter.formatDate(patient.birthdate)} (
              {patient.dateOfDeath ? (
                <>
                  <span style={{ fontFamily: 'Segoe UI Symbol' }}>
                    &#x271D;
                  </span>{' '}
                  {DefaultDateFormatter.formatDate(patient.dateOfDeath)}
                </>
              ) : (
                <>
                  {intl('view.patient.age')}:{' '}
                  {calculateYearsForDate(new Date(patient.birthdate))}
                </>
              )}
              )
            </span>
          )}
          {!ssnHidden && (
            <span>
              <b>{intl('view.patient.ssn')}</b>:{' '}
              {getValueOrNotSpecifiedString(patient.svnr)}
            </span>
          )}
          {!birthPlaceHidden && birthPlace && (
            <span>
              <b>{intl('view.patient.birthplace')}</b>:{' '}
              {intlCountry(birthPlace.country)}
              {birthPlace.city && <>, {birthPlace.city}</>}
            </span>
          )}
          {!motherMaidenNameHidden && (
            <span>
              <b>{intl('view.patient.mothermaidenname')}</b>:{' '}
              {getValueOrNotSpecifiedString(patient.name?.motherMaidenName)}
            </span>
          )}
        </VBox>
      </HBox>
    </PatientDetailsSection>
  );
}
